import "./Homepage.css";
import { useState, useEffect, cache } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useAuth } from "../context/AuthContext";

function HomePage({ setPage }) {
  const [login, setLogin] = useState(false);
  const [user, setUser] = useState('');
  const [userId, setUserId] = useState(null);
  const [userData, setUserData] = useState();
  const [sessionId, setSessionId] = useState(null);
  const [expired, setExpired] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [loading, setLoading] = useState(false);
  const [adminEmail, setAdminEmail] = useState(null);

  // ✅ Device check state
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [deviceChecked, setDeviceChecked] = useState(false);

  const { isAdmin, isContextloading, email } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Plan options
  const planOptions = [
    { value: 'visitor', label: 'Visitor Level (Free)' },
    { value: 'operator', label: 'Operator Training Package' },
    { value: 'engineer', label: 'Process Engineer Suite' },
    { value: 'classroom', label: 'Classroom' },
    { value: 'admin', label: 'Administrator' }
  ];

  // Plan to page mapping
  const planPageMapping = {
    'visitor': 'CrudeTank1',
    'operator': 'OperatorDashboard',
    'engineer': 'EngineerDashboard',
    'classroom': 'ClassroomDashboard'
  };

  // ✅ Minimum screen width for simulator access (laptop size)
  const MIN_SIMULATOR_WIDTH = 1024;

  // ✅ Detect device on mount + on resize
  useEffect(() => {
    const checkScreen = () => {
      const isSmall = window.innerWidth < MIN_SIMULATOR_WIDTH;
      setIsSmallScreen(isSmall);
      setDeviceChecked(true);
    };

    checkScreen();
    window.addEventListener('resize', checkScreen);
    return () => window.removeEventListener('resize', checkScreen);
  }, []);

  // ✅ Show alert on mount if small screen
  useEffect(() => {
    if (!deviceChecked) return;
    if (!isSmallScreen) return;

    Swal.fire({
      icon: 'warning',
      title: 'Screen Too Small',
      html: `
        <div style="text-align: left; color: #e8e8e8; font-family: 'Inter', 'Segoe UI', -apple-system, sans-serif;">
          <p style="margin: 0 0 16px 0; line-height: 1.7; font-size: 14px; color: #b8c8d8;">
            The <strong style="color: #ffffff;">NK Tech Refinery DCS Simulator</strong> is a full-scale industrial control system. It requires a <strong style="color: #00ff88;">laptop, desktop, or large-screen device</strong> to display the process diagrams, control panels, and instrument tags properly.
          </p>
          <div style="background: rgba(255,255,255,0.03); padding: 16px 18px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.08); margin-bottom: 14px;">
            <div style="color: #8aa4c8; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; font-weight: 600;">
              Recommended Devices
            </div>
            <ul style="margin: 0; padding: 0; list-style: none; line-height: 1.9; font-size: 13px; color: #a8b8c8;">
              <li>▸ Laptop (13" or larger)</li>
              <li>▸ Desktop monitor (1024px width or more)</li>
              <li>▸ Tablet in landscape (large screens only)</li>
            </ul>
          </div>
          <div style="background: rgba(255, 190, 0, 0.08); padding: 12px 16px; border-radius: 6px; border: 1px solid rgba(255, 190, 0, 0.2);">
            <p style="color: #ffbe00; margin: 0; font-size: 13px; line-height: 1.6;">
              ⚠ Please switch to a larger device to access the simulator.
            </p>
          </div>
        </div>
      `,
      confirmButtonText: 'Understood',
      confirmButtonColor: '#1e3a4a',
      width: 600,
      padding: '24px',
      background: '#0d1a2a',
      color: '#e8e8e8',
      backdrop: 'rgba(0,0,0,0.85)',
      allowOutsideClick: false,
      allowEscapeKey: true
    });
  }, [deviceChecked, isSmallScreen]);

  const isUserPlanAvailable = async (plan, email) => {
    const response = await fetch(`https://api.nktechhub.com/api/refinery/plan-availability?plan=${plan}&email=${email}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    return { status: response.status, statusText: response.statusText, ok: response.ok };
  };

  const savedUserPlanPermissions = (userPlan) => {
    const localStore = localStorage.getItem('user_plan_permissions');
    const getUserPlanPermissions = JSON.parse(localStore);
    if (localStore) {
      if (userPlan !== getUserPlanPermissions.plan) {
        clearPlanData();
        const cacheData = {
          plan: userPlan,
          allowedPages: null,
          planName: null,
          timestamp: Date.now()
        };
        localStorage.setItem('user_plan_permissions', JSON.stringify(cacheData));
        return false;
      }
      return true;
    }

    const cacheData = {
      plan: userPlan,
      allowedPages: null,
      planName: null,
      timestamp: Date.now()
    };
    localStorage.setItem('user_plan_permissions', JSON.stringify(cacheData));

    return false;
  };

  const handleAdminAccess = (selectedPlan) => {
    if (selectedPlan === 'classroom' || selectedPlan === 'admin') {
      return;
    }

    const localStore = localStorage.getItem('user_plan_permissions');

    if (localStore) {
      try {
        const getUserPlanPermissions = JSON.parse(localStore);

        if (selectedPlan !== getUserPlanPermissions.plan) {
          clearPlanData();

          const cacheData = {
            plan: selectedPlan,
            allowedPages: null,
            planName: null,
            timestamp: Date.now()
          };
          localStorage.setItem('user_plan_permissions', JSON.stringify(cacheData));
        }
      } catch (e) {
        console.error('Error parsing permissions:', e);
        localStorage.removeItem('user_plan_permissions');
      }
    } else {
      const cacheData = {
        plan: selectedPlan,
        allowedPages: null,
        planName: null,
        timestamp: Date.now()
      };
      localStorage.setItem('user_plan_permissions', JSON.stringify(cacheData));
    }

    Swal.fire({
      icon: 'success',
      title: 'Access Granted',
      text: 'Redirecting to Admin Panel...',
      timer: 1500,
      showConfirmButton: false
    }).then(() => {
      setPage("CrudeTank1");
    });
  };

  const clearPlanData = () => {
    localStorage.removeItem('user_plan_permissions');
    localStorage.removeItem('user_plan');
    localStorage.removeItem('user_access_level');
  };

  useEffect(() => {
    const fetchUserData = async () => {
      let token = localStorage.getItem('token');
      if (token) {
        try {
          const userInfo = await fetch(`https://api.nktechhub.com/user/v1/get-data`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (!userInfo.ok) {
            throw new Error('Failed to get user data');
          }

          const parsedData = await userInfo.json();
          setUserId(parsedData.user.id);
          setUserData(parsedData.user);
          window.history.replaceState({}, document.title, window.location.pathname);

          return;
        } catch (error) {
          console.error('Error parsing localStorage data:', error);
        }
      }

      const params = new URLSearchParams(window.location.search);
      token = params.get('token');
      localStorage.setItem('token', token);

      try {
        const response = await fetch(`https://api.nktechhub.com/user/v1/get-data`, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Failed to get session data');
        }

        const data = await response.json();
        setUserId(data.user.id);
        setUserData(data.user);

        window.history.replaceState({}, document.title, window.location.pathname);

      } catch (error) {
        console.error('❌ Error fetching session:', error);
      }
    };

    fetchUserData();
  }, []);

  const handleSignIn = async () => {
    // ✅ Block access on small screens
    if (isSmallScreen) {
      Swal.fire({
        icon: 'error',
        title: 'Device Not Supported',
        text: 'The simulator cannot be accessed on mobile, tablet, or small-screen devices. Please use a laptop or desktop.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#1e3a4a',
        background: '#0d1a2a',
        color: '#e8e8e8',
        backdrop: 'rgba(0,0,0,0.85)'
      });
      return;
    }

    setLoading(true);

    if (userData) {
      if (!selectedPlan) {
        Swal.fire({
          icon: 'warning',
          title: 'Select a Plan',
          text: 'Please select your purchased plan before continuing.',
          confirmButtonText: 'OK'
        }).then((result) => {
          setLoading(false);
        })

        return;
      }

      if (isAdmin) {
        handleAdminAccess(selectedPlan);
      }

      if (selectedPlan === 'admin') {
        Swal.fire({
          title: "Access Restricted",
          text: "Admin access is restricted to authorized personnel only.",
          icon: "info",
          input: "password",
          inputPlaceholder: "Enter password",
          inputAttributes: {
            autocapitalize: "off",
            autocorrect: "off"
          },
          showCancelButton: true,
          confirmButtonText: "Access Simulator",
          cancelButtonText: "Cancel",
          preConfirm: (password) => {
            if (!password) {
              Swal.showValidationMessage("Password is required");
              return false;
            }
            return password;
          }
        }).then(async (result) => {

          if (result.dismiss === Swal.DismissReason.cancel || result.dismiss === Swal.DismissReason.backdrop) {
            setLoading(false);
            return;
          }

          if (result.isConfirmed) {
            const enteredPassword = result.value;
            const userEmail = userData?.userData?.email || userData?.email;

            try {
              const response = await fetch(`https://api.nktechhub.com/api/dcs/adminpassword?password=${enteredPassword}&email=${userEmail}`, {
                credentials: 'include'
              });

              if (!response.ok) {
                throw new Error('Failed to get session data');
              }

              const data = await response.json();

              if (data.message === "Password verified") {
                savedUserPlanPermissions(selectedPlan);
                setLoading(false);
                Swal.fire({
                  icon: "success",
                  title: "Access Granted",
                  text: "Redirecting...",
                  timer: 1500,
                  showConfirmButton: false
                }).then(() => {
                  setPage("CrudeTank1");
                });
              } else {
                setLoading(false);
                Swal.fire({
                  icon: "error",
                  title: "Wrong Password",
                  text: data.message || "Incorrect password. Please contact your instructor for access.",
                  confirmButtonText: "Try Again"
                });
              }
            } catch (error) {
              console.error('❌ Error fetching password:', error);
              setLoading(false);
              Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to verify password. Please try again later."
              });
            }
          }
        });
      } else if (selectedPlan === 'classroom') {
        Swal.fire({
          title: 'Classroom Plan',
          html: `
    <div style="text-align: left; color: #e8e8e8; font-family: 'Inter', 'Segoe UI', -apple-system, sans-serif;">
      <div style="text-align: center; margin-bottom: 24px;">
        <span style="display: inline-block; background: #1e2a3a; color: #8aa4c8; padding: 4px 20px; border-radius: 20px; font-size: 11px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; border: 1px solid #2a3a4a;">
          ● In Development
        </span>
      </div>

      <h3 style="color: #ffffff; font-weight: 500; font-size: 20px; margin-bottom: 12px; letter-spacing: -0.3px;">Educational Access</h3>
      
      <p style="margin-bottom: 20px; line-height: 1.7; font-size: 14px; color: #b8c8d8;">
        Designed for <strong style="color: #ffffff; font-weight: 500;">educational institutions</strong> 
        and <strong style="color: #ffffff; font-weight: 500;">training centers</strong>. Enables instructors to deliver refinery operations training in a controlled, interactive environment.
      </p>
      
      <div style="background: rgba(255, 255, 255, 0.02); padding: 18px 20px; border-radius: 8px; margin: 14px 0; border: 1px solid rgba(255, 255, 255, 0.06);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
          <h4 style="color: #8aa4c8; font-weight: 500; font-size: 13px; margin: 0; letter-spacing: 0.5px; text-transform: uppercase;">Student Capacity</h4>
          <span style="color: #ffffff; font-size: 14px; font-weight: 500;">10 – 500</span>
        </div>
        <div style="height: 3px; background: linear-gradient(90deg, #2a4a6a, #4a8aaa, #2a4a6a); border-radius: 2px; margin-bottom: 12px;"></div>
        <ul style="margin: 0; padding: 0; list-style: none; line-height: 1.8; font-size: 13px; color: #a8b8c8;">
          <li style="padding: 2px 0; display: flex; align-items: center; gap: 8px;">
            <span style="color: #4a8aaa;">—</span> Small classes (10-50) — Specialized training groups
          </li>
          <li style="padding: 2px 0; display: flex; align-items: center; gap: 8px;">
            <span style="color: #4a8aaa;">—</span> Medium classes (50-200) — University departments
          </li>
          <li style="padding: 2px 0; display: flex; align-items: center; gap: 8px;">
            <span style="color: #4a8aaa;">—</span> Large classes (200-500) — Entire institutions
          </li>
        </ul>
      </div>

      <div style="background: rgba(255, 255, 255, 0.02); padding: 18px 20px; border-radius: 8px; margin: 14px 0; border: 1px solid rgba(255, 255, 255, 0.06);">
        <h4 style="color: #8aa4c8; font-weight: 500; font-size: 13px; margin: 0 0 10px 0; letter-spacing: 0.5px; text-transform: uppercase;">Key Features</h4>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px 16px;">
          <ul style="margin: 0; padding: 0; list-style: none; line-height: 1.8; font-size: 13px; color: #a8b8c8;">
            <li style="padding: 2px 0;">▸ Instructor dashboard</li>
            <li style="padding: 2px 0;">▸ Student management</li>
            <li style="padding: 2px 0;">▸ Real-time tracking</li>
          </ul>
          <ul style="margin: 0; padding: 0; list-style: none; line-height: 1.8; font-size: 13px; color: #a8b8c8;">
            <li style="padding: 2px 0;">▸ Pre-built scenarios</li>
            <li style="padding: 2px 0;">▸ Performance reports</li>
            <li style="padding: 2px 0;">▸ Multi-user access</li>
          </ul>
        </div>
      </div>

      <div style="background: rgba(255, 255, 255, 0.02); padding: 18px 20px; border-radius: 8px; margin: 14px 0; border: 1px solid rgba(255, 255, 255, 0.06);">
        <h4 style="color: #8aa4c8; font-weight: 500; font-size: 13px; margin: 0 0 10px 0; letter-spacing: 0.5px; text-transform: uppercase;">Applications</h4>
        <ul style="margin: 0; padding: 0; list-style: none; line-height: 1.8; font-size: 13px; color: #a8b8c8;">
          <li style="padding: 2px 0;">▸ Universities — Chemical and petroleum engineering</li>
          <li style="padding: 2px 0;">▸ Technical schools — Refinery operations training</li>
          <li style="padding: 2px 0;">▸ Corporate training — Employee onboarding</li>
          <li style="padding: 2px 0;">▸ Research — Process optimization studies</li>
        </ul>
      </div>

      <div style="background: #0a1a2a; padding: 16px 20px; border-radius: 8px; margin: 14px 0; border: 1px solid rgba(74, 138, 170, 0.15);">
        <p style="color: #8aa4c8; margin: 0; font-size: 13px; line-height: 1.6; text-align: center;">
          ⚡ This plan is currently in development. Updates will be announced.
        </p>
      </div>

      <div style="background: rgba(255, 255, 255, 0.015); padding: 14px 20px; border-radius: 6px; margin: 14px 0; border: 1px solid rgba(255, 255, 255, 0.04);">
        <ul style="margin: 0; padding: 0; list-style: none; line-height: 1.8; font-size: 12px; color: #6a7a8a;">
          <li style="padding: 1px 0;">◈ Strictly for educational and training purposes</li>
          <li style="padding: 1px 0;">◈ Not intended for commercial refinery operations</li>
          <li style="padding: 1px 0;">◈ Requires instructor approval for activation</li>
        </ul>
      </div>
    </div>
  `,
          icon: 'info',
          confirmButtonText: 'Got it',
          cancelButtonText: 'Close',
          showCancelButton: true,
          showCloseButton: true,
          width: 660,
          padding: '28px',
          background: '#0d1a2a',
          color: '#e8e8e8',
          confirmButtonColor: '#1e3a4a',
          cancelButtonColor: '#1a1a2a',
          backdrop: 'rgba(0,0,0,0.85)',
          customClass: {
            container: 'classroom-swal-container',
            popup: 'classroom-swal-popup',
            confirmButton: 'classroom-confirm-btn',
            cancelButton: 'classroom-cancel-btn'
          }
        }).then((result) => {
          if (result.dismiss === Swal.DismissReason.cancel ||
            result.dismiss === Swal.DismissReason.backdrop ||
            result.dismiss === Swal.DismissReason.close) {
            setLoading(false);
            return;
          }

          if (result.isConfirmed) {
            setLoading(false);
            return;
          }
        });
      } else if (selectedPlan === 'engineer') {
        const locallySaved = savedUserPlanPermissions(selectedPlan);
        if (locallySaved) {
          setLoading(false);
          Swal.fire({
            icon: "success",
            title: "Access Granted",
            text: "Redirecting...",
            timer: 1500,
            showConfirmButton: false
          }).then(() => {
            setPage("CrudeTank1");
          });
          return;
        }
        const response = await isUserPlanAvailable('engineer', userData?.userData?.email || userData?.email)
        if (response.status === 200) {

          setLoading(false);
          Swal.fire({
            icon: "success",
            title: "Access Granted",
            text: "Redirecting...",
            timer: 1500,
            showConfirmButton: false
          }).then(() => {
            setPage("CrudeTank1");
          });
        } else if (response.status === 404) {
          setLoading(false);
          Swal.fire({
            icon: 'error',
            title: `Plan ${response.statusText}`,
            text: 'You dont have access to this plan',
            confirmButtonText: 'ok'
          });
          return;
        }
        else {
          setLoading(false);
          Swal.fire({
            icon: 'error',
            title: `${response.statusText}`,
            confirmButtonText: 'ok'
          });
          return;
        }

      } else if (selectedPlan === 'operator') {
        const locallySaved = savedUserPlanPermissions(selectedPlan);
        if (locallySaved) {
          setLoading(false);
          Swal.fire({
            icon: "success",
            title: "Access Granted",
            text: "Redirecting...",
            timer: 1500,
            showConfirmButton: false
          }).then(() => {
            setPage("CrudeTank1");
          });
          return;
        }
        const response = await isUserPlanAvailable('operator', userData?.userData?.email || userData?.email)
        if (response.status === 200) {
          setLoading(false);
          Swal.fire({
            icon: "success",
            title: "Access Granted",
            text: "Redirecting...",
            timer: 1500,
            showConfirmButton: false
          }).then(() => {
            setPage("CrudeTank1");
          });
        } else if (response.status === 404) {
          setLoading(false);
          Swal.fire({
            icon: 'error',
            title: `Plan ${response.statusText}`,
            text: 'You dont have access to this plan',
            confirmButtonText: 'ok'
          });
          return;
        }
        else {
          setLoading(false);
          Swal.fire({
            icon: 'error',
            title: `${response.statusText}`,
            confirmButtonText: 'ok'
          });
          return;
        }
      } else if (selectedPlan === 'visitor') {
        savedUserPlanPermissions(selectedPlan);
        setLoading(false);
        Swal.fire({
          icon: "success",
          title: "Access Granted",
          text: "Redirecting...",
          timer: 1500,
          showConfirmButton: false
        }).then(() => {
          setPage("CrudeTank1");
        });
      }

      else {
        Swal.fire({
          icon: 'warning',
          title: 'Still working on this feature',
          text: 'Please try again later.',
          showCancelButton: true,
          cancelButtonText: 'Cancel'
        }).then(() => {
          setLoading(false);
        });
      }
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'You are not logged in!',
        text: 'Please sign in to continue.',
        showCancelButton: true,
        confirmButtonText: 'Sign In',
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = "https://nktechhub.com/signup";
        }
      });
    }
  };

  const handlePlanChange = (e) => {
    setSelectedPlan(e.target.value);
  };

  return (
    <div className="page-container">
      {/* User Profile */}
      {userData && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          padding: '10px 20px',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          zIndex: 9999,
          transition: 'all 0.3s ease',
          cursor: 'default',
          fontFamily: 'Inter, -apple-system, sans-serif'
        }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.02)';
            e.currentTarget.style.boxShadow = '0 12px 48px rgba(0, 0, 0, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)';
          }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #00ff88, #00cc66)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#001018',
            flexShrink: 0
          }}>
            {userData.firstName?.[0] || userData.userData.firstName?.[0] || 'U'}
          </div>
          <div>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#ffffff',
              lineHeight: 1.2
            }}>
              {userData.firstName || userData.userData.firstName || 'User'}
            </div>
            <div style={{
              fontSize: '11px',
              color: 'rgba(255,255,255,0.6)',
              lineHeight: 1.2
            }}>
              {isAdmin ? 'Admin' : 'Welcome'}
            </div>
          </div>
          <div style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: '#00ff88',
            boxShadow: '0 0 12px rgba(0, 255, 136, 0.6)',
            flexShrink: 0
          }} />
        </div>
      )}

      {/* Animated Background Elements */}
      <div className="bg-effects">
        <div className="glow-orb orb-1"></div>
        <div className="glow-orb orb-2"></div>
        <div className="glow-orb orb-3"></div>
      </div>

      {/* HEADER */}
      <div className="page-header">
        <div className="header-badge">
          <span className="status-dot"></span>
          SYSTEM ONLINE • 
        </div>
        <h1>
          <span className="highlight">NK TECH</span>
          <br />
          REFINERY DCS SYSTEM
        </h1>
        <div className="header-sub">
          <span className="divider-line"></span>
          <h2>30,000 BPD MODULAR REFINERY</h2>
          <span className="divider-line"></span>
        </div>
        <p className="header-description">
          Distributed Control System for Real-Time Refinery Operations
        </p>
      </div>

      {/* BODY - Cards Grid */}
      <div className="page-body">
        {/* Card 1: Process Overview */}
        <div className="dcs-panel panel-1">
          <div className="panel-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
          </div>
          <div className="panel-content">
            <h3>PROCESS OVERVIEW</h3>
            <p>
              This Distributed Control System (DCS) simulates refinery-wide operations
              including crude receiving, tank farm operations, preheat train systems,
              desalting, fractionation, stabilization, utility systems, and refinery
              process monitoring.
            </p>
            <div className="panel-tag">Real-Time Monitoring</div>
          </div>
        </div>

        {/* Card 2: Industrial Control */}
        <div className="dcs-panel panel-2">
          <div className="panel-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>
          </div>
          <div className="panel-content">
            <h3>INDUSTRIAL CONTROL</h3>
            <p>
              Operators can monitor process conditions, alarms, transmitters,
              control valves, pumps, furnaces, towers, and refinery process units
              in real time with full operational visibility.
            </p>
            <div className="panel-tag">SCADA Integration</div>
          </div>
        </div>

        {/* Card 3: Simulator Access */}
        <div className="dcs-panel panel-3">
          <div className="panel-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
          </div>
          <div className="panel-content">
            <h3>SIMULATOR ACCESS</h3>
            <p>
              Click the sign in button below to access the refinery DCS
              operational environment and begin monitoring your processes.
            </p>

            {/* ✅ Small screen warning */}
            {isSmallScreen && (
              <div style={{
                margin: '12px 0',
                padding: '12px 16px',
                borderRadius: '10px',
                background: 'rgba(255, 190, 0, 0.08)',
                border: '1px solid rgba(255, 190, 0, 0.25)',
                color: '#ffbe00',
                fontSize: '13px',
                lineHeight: 1.6,
                textAlign: 'left'
              }}>
                <strong style={{ display: 'block', marginBottom: '4px' }}>⚠ Device Not Supported</strong>
                The simulator requires a laptop or desktop screen (min width 1024px). Please switch to a larger device.
              </div>
            )}

            {/* Plan Selection Dropdown */}
            <div style={{
              margin: '15px 0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px'
            }}>
              <label style={{
                color: '#b8c7d6',
                fontSize: '14px',
                fontWeight: '500',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>
                Select Your Plan
              </label>
              <select
                value={selectedPlan}
                onChange={handlePlanChange}
                style={{
                  width: '100%',
                  maxWidth: '280px',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: 'rgba(255,255,255,0.05)',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  paddingRight: '36px'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#00ff88';
                  e.target.style.boxShadow = '0 0 20px rgba(0, 255, 136, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255,255,255,0.15)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="" style={{ background: '#1a2332', color: '#8899aa' }}>
                  -- Select your plan --
                </option>
                <option value="visitor" style={{ background: '#1a2332', color: '#ffffff' }}>
                  Visitor Level (Free)
                </option>
                <option value="operator" style={{ background: '#1a2332', color: '#ffffff' }}>
                  Operator Training Package
                </option>
                <option value="engineer" style={{ background: '#1a2332', color: '#ffffff' }}>
                  Process Engineer Suite
                </option>
                <option value="classroom" style={{ background: '#1a2332', color: '#ffffff' }}>
                  Classroom
                </option>
                <option value="admin" style={{ background: '#1a0032', color: '#ffffff' }}>
                  Administrator
                </option>
              </select>
            </div>

            {/* Show selected plan info */}
            {selectedPlan && (
              <div style={{
                marginBottom: '12px',
                padding: '8px 16px',
                borderRadius: '8px',
                background: 'rgba(0, 255, 136, 0.08)',
                border: '1px solid rgba(0, 255, 136, 0.15)',
                color: '#00ff88',
                fontSize: '13px',
                display: 'inline-block'
              }}>
                ✅ Selected: {planOptions.find(p => p.value === selectedPlan)?.label}
              </div>
            )}

            <button
              className="signin-btn"
              onClick={handleSignIn}
              disabled={isSmallScreen}
              style={isSmallScreen ? {
                opacity: 0.5,
                cursor: 'not-allowed',
                filter: 'grayscale(0.5)'
              } : {}}
            >
              <span className="btn-icon">
                {loading ? (
                  <svg className="spinner" viewBox="0 0 50 50" style={{
                    width: '20px',
                    height: '20px',
                    animation: 'spin 0.8s linear infinite'
                  }}>
                    <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" strokeWidth="4"
                      strokeDasharray="31.4 31.4" strokeLinecap="round" />
                  </svg>
                ) : (
                  '▶'
                )}
              </span>
              {loading ? 'PROCESSING...' : (isSmallScreen ? 'DEVICE NOT SUPPORTED' : 'ACCESS SIMULATOR')}
              <span className="btn-shimmer"></span>
            </button>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div className="page-footer">
        <div className="footer-stats">
          <div className="stat-item">
            <span className="stat-value">24/7</span>
            <span className="stat-label">Operations</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-value">99.9%</span>
            <span className="stat-label">Uptime</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-value">Secure</span>
            <span className="stat-label">Connection</span>
          </div>
        </div>
        <p className="footer-copyright">
          © {new Date().getFullYear()} NK Tech Refinery DCS System. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default HomePage;